import type { ProductSlugs } from '@deque/billing-utils';
import { Button, Icon, Select } from '@deque/cauldron-react';
import { IssueFieldSource } from '@deque/ui-export-jira';
import classNames from 'classnames';
import React, { type ChangeEvent, useCallback } from 'react';
import { Controller, type FieldError, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type SupportedIntegrationProductSlugs } from '~/common/constants';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import { getIntegrationProductInfoFromSlug } from '~/common/utils/get-integration-product-name-from-slug';
import {
  findAndCalculateDefaultValue,
  findAndCalculateSourceType,
  type SelectOption
} from '../FieldMapping';
import styles from './Field.css';
import FieldAssignment from './FieldAssignment';

export interface FieldProps {
  integrationProductSlug:
    | SupportedIntegrationProductSlugs
    | ProductSlugs.axeAuditorIntegration;
  axeFields: SelectOption[];
  mappingFields: SelectOption[];
  index: number;
  removeField: (index: number) => void;
  required?: boolean;
  onSelectIntegrationField: () => void;
  chosenIntegrationFields: string[];
  integrationProductLabels: string[];
}

const getFieldLabel = (
  mappingFields: SelectOption[],
  field: { value: string }
) => {
  return mappingFields.find(mappingField => mappingField.value === field.value)
    ?.label;
};

const Field = ({
  integrationProductSlug,
  axeFields,
  mappingFields,
  index,
  removeField,
  required = false,
  onSelectIntegrationField,
  chosenIntegrationFields,
  integrationProductLabels
}: FieldProps) => {
  const { t } = useTranslation();
  const narrow = useMediaQuery('(max-width: 37.5rem)');
  const {
    control,
    formState: { errors },
    setValue,
    getValues
  } = useFormContext();

  const getOptions = useCallback(
    (options: SelectOption[], field: { value: string }) =>
      options.filter(option => {
        const isAlreadySelected = chosenIntegrationFields.includes(
          option.value
        );
        const isCurrentField = field.value === option.value;
        const isEmptyOption = !option.value;

        return isCurrentField || isEmptyOption || !isAlreadySelected;
      }),
    [chosenIntegrationFields]
  );

  const getError = (
    field: { value: string },
    fieldError: typeof errors,
    i: number
  ) => {
    // Only display the error on the empty field
    if (!errors || field.value) {
      return undefined;
    }
    // The inferred type is incorrect, so we need to cast it
    return (fieldError.fieldMapping as unknown as FieldError[])?.[i]?.message;
  };

  const integrationName = getIntegrationProductInfoFromSlug(
    integrationProductSlug as ProductSlugs
  ).displayName;

  const onIntegrationFieldChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
      const fieldSourceType = findAndCalculateSourceType(mappingFields, value);

      const fieldConfig = {
        mappingField: value,
        sourceType: fieldSourceType,
        axeField: getValues(`fieldMapping.${index}.axeField`)
      };

      if (fieldSourceType === IssueFieldSource.FixedValue) {
        const axeFieldValue = findAndCalculateDefaultValue(
          mappingFields,
          value
        );

        fieldConfig.axeField = axeFieldValue;
      } else if (fieldSourceType !== IssueFieldSource.Axe) {
        fieldConfig.axeField = '';
      }

      setValue(`fieldMapping.${index}`, fieldConfig);

      onSelectIntegrationField();
    },
    [index, mappingFields, setValue, onSelectIntegrationField]
  );

  return (
    <div
      className={classNames(styles.fieldContainer, {
        [styles.narrow]: narrow
      })}
    >
      <p className={styles.map} id={`map-${index}`}>
        {t('Map')}
      </p>
      <Controller
        name={`fieldMapping.${index}.mappingField`}
        control={control}
        aria-labelledby={`integration-field-${index}`}
        render={({ field }) => (
          <Select
            {...field}
            id={`integration-field-${index}`}
            value={field.value}
            disabled={required}
            options={getOptions(mappingFields, field)}
            onChange={onIntegrationFieldChange}
            error={getError(field, errors, index)}
            aria-label={
              required
                ? undefined
                : t(`{{ integration }} field {{ index }}`, {
                    integration: integrationName,
                    index: index + 1
                  })
            }
            label={t(`{{ required }}{{ integration }} {{ fieldType }} Field`, {
              required: required ? t('Required ') : '',
              integration: integrationName,
              fieldType: required ? getFieldLabel(mappingFields, field) : ''
            })}
          />
        )}
      />

      <FieldAssignment
        axeFields={axeFields}
        mappingFields={mappingFields}
        index={index}
        control={control}
        getError={getError}
        errors={errors}
        getValues={getValues}
        t={t}
        integrationProductLabels={integrationProductLabels}
      />

      {!required && (
        <Button
          className={styles.removeButton}
          variant="secondary"
          onClick={() => removeField(index)}
        >
          <Icon
            type="minus"
            label={t('Remove mapping fields {{ index }}', { index })}
          />
          {narrow && t('Remove')}
        </Button>
      )}
    </div>
  );
};

export default Field;
